import { Button } from "antd";
import { getNotificationApi } from "../lib/antdAppApi";
import { restoreEntity } from "../services/trash/trash.service";
import { TrashEntity } from "../services/trash/trash.types";

export interface ShowDeleteUndoNotificationOptions {
  entity: TrashEntity;
  id: number;
  /** Nombre del elemento borrado, mostrado en el mensaje de la notificación. */
  title: string;
  /** Refresco de la lista tras una restauración exitosa. */
  onRestored: () => void | Promise<void>;
}

const UNDO_NOTIFICATION_DURATION_SECONDS = 12;

/**
 * Muestra la notificación de éxito al eliminar un elemento (curso, lección,
 * recurso, programa o proyecto) con un botón "Deshacer" que lo restaura desde
 * la papelera sin recargar la página.
 */
export const showDeleteUndoNotification = ({
  entity,
  id,
  title,
  onRestored,
}: ShowDeleteUndoNotificationOptions): void => {
  // Context-bound instance from antd's <App> (see lib/antdAppApi): the static
  // notification API does not render under React 19.
  const notification = getNotificationApi();
  const key = `delete-undo-${entity}-${id}-${Date.now()}`;
  const loadingKey = `${key}-loading`;

  // Closure flag (no hooks available outside components): guards against a
  // double click on "Deshacer" firing two concurrent restores.
  let restoring = false;

  const handleUndo = async () => {
    if (restoring) return;
    restoring = true;

    notification.destroy(key);
    notification.open({
      key: loadingKey,
      message: `Restaurando "${title}"...`,
      duration: 0,
      btn: (
        <Button size="small" loading disabled>
          Deshacer
        </Button>
      ),
    });

    try {
      const result = await restoreEntity(entity, id);
      notification.destroy(loadingKey);
      notification.success({ message: "Elemento restaurado" });
      if (result.warnings.length > 0) {
        notification.warning({
          message: "Advertencias al restaurar",
          description: result.warnings.join(" "),
        });
      }
      await onRestored();
    } catch (error: any) {
      notification.destroy(loadingKey);
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message ?? "No se pudo restaurar el elemento.",
      });
    } finally {
      restoring = false;
    }
  };

  notification.open({
    key,
    message: `Se eliminó "${title}"`,
    duration: UNDO_NOTIFICATION_DURATION_SECONDS,
    btn: (
      <Button type="link" size="small" onClick={handleUndo}>
        Deshacer
      </Button>
    ),
  });
};
